import api from "src/common/axiosInstance";

const base = (workspaceId) => `/workspaces/${workspaceId}/documents`;
const config = (workspaceId, extra = {}) => ({ headers: { "X-Workspace-ID": workspaceId }, ...extra });
const body = (response) => response.data;

export const documentOptions = (workspaceId, signal) => api.get(`${base(workspaceId)}/options`, config(workspaceId, { signal })).then(body);
export const listDocuments = (workspaceId, params, signal) => api.get(base(workspaceId), config(workspaceId, { params, signal })).then(body);
export const listCategories = (workspaceId, signal) => api.get(`/workspaces/${workspaceId}/document-categories`, config(workspaceId, { signal })).then(body);
export const saveCategory = (workspaceId, id, payload) => (id
  ? api.patch(`/workspaces/${workspaceId}/document-categories/${id}`, payload, config(workspaceId))
  : api.post(`/workspaces/${workspaceId}/document-categories`, payload, config(workspaceId))).then(body);
export const initiateUpload = (workspaceId, payload) => api.post(`${base(workspaceId)}/uploads`, payload, config(workspaceId)).then(body);
export const completeUpload = (workspaceId, id) => api.post(`${base(workspaceId)}/${id}/complete`, {}, config(workspaceId)).then(body);
export const cancelUpload = (workspaceId, id) => api.delete(`${base(workspaceId)}/${id}/upload`, config(workspaceId)).then(body);
export const updateDocument = (workspaceId, id, payload) => api.patch(`${base(workspaceId)}/${id}`, payload, config(workspaceId)).then(body);
export const assignDocuments = (workspaceId, ids, leadId) => api.put(`${base(workspaceId)}/assignment`, { document_ids: ids, lead_id: leadId }, config(workspaceId)).then(body);
export const matchDocuments = (workspaceId, ids, refresh = false, signal) => api.post(`${base(workspaceId)}/matches`, {
  document_ids: ids, ...(refresh ? { refresh: true } : {}),
}, config(workspaceId, { signal })).then(body);
export const approveDocumentMatches = (workspaceId, assignments) => api.put(`${base(workspaceId)}/matches/approve`, { assignments }, config(workspaceId)).then(body);
export const archiveDocument = (workspaceId, id) => api.delete(`${base(workspaceId)}/${id}`, config(workspaceId)).then(body);
export const restoreDocument = (workspaceId, id) => api.post(`${base(workspaceId)}/${id}/restore`, {}, config(workspaceId)).then(body);
export const searchDocumentLeads = (workspaceId, search, signal) => api.get("/leads", config(workspaceId, { params: { search, per_page: 20 }, signal })).then(body);

const storageBase = (workspaceId) => `/workspaces/${workspaceId}/document-storage`;
export const getDocumentStorage = (workspaceId, signal) => api.get(storageBase(workspaceId), config(workspaceId, { signal })).then(body);
export const saveDocumentStorage = (workspaceId, id, payload) => (id
  ? api.put(`${storageBase(workspaceId)}/${id}`, payload, config(workspaceId))
  : api.post(storageBase(workspaceId), payload, config(workspaceId))).then(body);
export const selectDocumentStorage = (workspaceId, id, active) => api.post(`${storageBase(workspaceId)}/${id}/${active ? "activate" : "pause"}`, {}, config(workspaceId)).then(body);

export async function documentPlayback(workspaceId, id, signal) {
  const { data } = await api.post(`${base(workspaceId)}/${id}/playback`, {}, config(workspaceId, { signal }));
  const url = new URL(data.url);
  const r2 = /^[a-f0-9]{32}(?:\.(?:eu|us|fedramp))?\.r2\.cloudflarestorage\.com$/.test(url.hostname);
  const cloudinary = url.hostname === "api.cloudinary.com" && /^\/v1_1\/[a-z0-9_-]+\/asset\/download$/.test(url.pathname);
  if (url.protocol !== "https:" || url.port || url.username || url.password || (!r2 && !cloudinary)
    || !["audio", "video"].includes(data.kind) || !data.mime_type?.startsWith(`${data.kind}/`)
    || !Number.isFinite(data.expires_in) || data.expires_in <= 0 || data.expires_in > 900) {
    throw new Error("The recording playback response is invalid. Please contact your administrator.");
  }
  return data;
}

export async function downloadDocument(workspaceId, id) {
  const response = await api.post(`${base(workspaceId)}/${id}/download`, {}, config(workspaceId));
  const url = new URL(response.data.url);
  if (url.protocol !== "https:") throw new Error("The download URL is not secure.");
  const link = document.createElement("a");
  link.href = url.href;
  link.referrerPolicy = "no-referrer";
  document.body.appendChild(link);
  link.click();
  link.remove();
}

// Deliberately not the authenticated CRM axios instance. Never send CRM tokens,
// cookies, workspace headers, or XSRF credentials to either storage provider.
export function uploadToStorage(upload, file, signal, onProgress) {
  return new Promise((resolve, reject) => {
    const url = new URL(upload.url);
    if (url.protocol !== "https:") { reject(new Error("The upload URL is not secure.")); return; }
    const xhr = new XMLHttpRequest();
    const aborted = () => new DOMException("Upload cancelled", "AbortError");
    if (signal.aborted) { reject(aborted()); return; }
    const abort = () => xhr.abort();
    const finish = (error) => {
      signal.removeEventListener("abort", abort);
      if (error) reject(error); else resolve();
    };
    const cloudinary = upload.provider === "cloudinary";
    if (cloudinary && (url.hostname !== "api.cloudinary.com" || url.port || url.username || url.password || !/^\/v1_1\/[a-z0-9_-]+\/(image|video|raw)\/upload$/.test(url.pathname))) {
      reject(new Error("The Cloudinary upload destination is invalid.")); return;
    }
    xhr.open(cloudinary ? "POST" : "PUT", url.href);
    xhr.withCredentials = false;
    xhr.timeout = 60 * 60 * 1000;
    let payload = file;
    if (cloudinary) {
      payload = new FormData();
      Object.entries(upload.fields).forEach(([name, value]) => payload.append(name, String(value)));
      payload.append("file", file);
      // Browser supplies the multipart boundary. Never set Content-Type manually.
    } else xhr.setRequestHeader("Content-Type", upload.headers["Content-Type"]);
    xhr.upload.onprogress = (event) => { if (event.lengthComputable) onProgress(Math.round(event.loaded * 100 / event.total)); };
    xhr.onload = () => finish(xhr.status >= 200 && xhr.status < 300 ? null : new Error(cloudinary
      ? `Cloudinary upload failed (${xhr.status}). Check account limits and API permissions, or retry.`
      : `Upload failed (${xhr.status}). Check the R2 CORS configuration or retry.`));
    xhr.onerror = () => finish(new Error(cloudinary ? "Upload could not reach Cloudinary. Check your connection and retry."
      : "Upload could not reach Cloudflare. Check your connection and the bucket CORS configuration."));
    xhr.ontimeout = () => finish(new Error("Upload timed out. Please retry."));
    xhr.onabort = () => finish(aborted());
    signal.addEventListener("abort", abort, { once: true });
    xhr.send(payload);
  });
}

export function documentError(error) {
  return Object.values(error?.response?.data?.errors || {}).flat()[0] || error?.response?.data?.message || error?.message || "Something went wrong. Please retry.";
}

export function formatBytes(value) {
  if (value < 1024) return `${value} B`;
  const units = ["KB", "MB", "GB"];
  let number = value / 1024; let unit = 0;
  while (number >= 1024 && unit < units.length - 1) { number /= 1024; unit++; }
  return `${number.toFixed(number >= 10 ? 0 : 1)} ${units[unit]}`;
}

export function validateDocumentFile(file, options) {
  const extension = file.name.split(".").pop().toLowerCase();
  if (!options.extensions.includes(extension)) return "Unsupported file format.";
  if (!file.size) return "Empty files cannot be uploaded.";
  const maxBytes = options.max_bytes_by_extension?.[extension] ?? options.max_bytes;
  if (file.size > maxBytes) return `Maximum file size is ${formatBytes(maxBytes)} for .${extension} files.`;
  const invalidName = [...file.name].some((character) => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127 || ["/", "\\"].includes(character));
  if (file.name.length > 200 || invalidName) return "Use a shorter file name without path separators or control characters.";
  return "";
}
